import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

const ELEMENTS = ['H1', 'H2', 'H3', 'Body', 'Code'];
const DEFAULT_SIZES = { H1: 36, H2: 28, H3: 22, Body: 16, Code: 14 };
const DEFAULT_WEIGHTS = { H1: 700, H2: 700, H3: 600, Body: 400, Code: 400 };
const DEFAULT_PREVIEWS = {
  H1: 'The quick brown fox',
  H2: 'Jumps over the lazy dog',
  H3: 'Pack my box with five dozen liquor jugs',
  Body: 'The quick brown fox jumps over the lazy dog. Sphinx of black quartz, judge my vow. How vexingly quick daft zebras jump.',
  Code: 'const hello = "world";\nconsole.log(hello);',
};

function FontPicker({ value, onChange, fontFamilies, theme }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = fontFamilies.filter(f =>
    !search || f.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'relative', marginBottom: 6 }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        background: theme.bgInput,
        border: `1px solid ${open ? theme.accentBorder : theme.borderMedium}`,
        borderRadius: 6, overflow: 'hidden',
      }}>
        <input
          type="text"
          value={open ? search : (value || '')}
          placeholder={open ? 'Search fonts…' : '— Select font —'}
          onFocus={() => { setOpen(true); setSearch(''); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '6px 8px',
            background: 'none', border: 'none',
            color: value && !open ? theme.text : theme.textMuted,
            fontSize: 11, fontFamily: 'inherit', outline: 'none', cursor: 'text',
          }}
        />
        <span style={{ padding: '0 8px', color: theme.textGhost, fontSize: 9, flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: theme.bgElevated, border: `1px solid ${theme.borderHover}`,
          borderRadius: 6, boxShadow: theme.shadow,
          maxHeight: 220, overflowY: 'auto', marginTop: 2,
        }}>
          <div
            onMouseDown={() => { onChange(''); setOpen(false); setSearch(''); }}
            style={{ padding: '6px 10px', fontSize: 11, color: theme.textFaint, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >— None —</div>
          {filtered.map(f => (
            <div
              key={f}
              onMouseDown={() => { onChange(f); setOpen(false); setSearch(''); }}
              style={{
                padding: '6px 10px', fontSize: 11, cursor: 'pointer',
                color: f === value ? theme.accent : theme.text,
                fontWeight: f === value ? 600 : 400,
                background: f === value ? theme.accentBg : 'none',
              }}
              onMouseEnter={e => { if (f !== value) e.currentTarget.style.background = theme.bgHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = f === value ? theme.accentBg : 'none'; }}
            >{f}</div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 11, color: theme.textFaint }}>No fonts match</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CSSPlayground({ onClose }) {
  const { theme } = useTheme();

  const [assignments, setAssignments] = useState(() => {
    const init = {};
    for (const el of ELEMENTS) {
      init[el] = { family: '', size: DEFAULT_SIZES[el], weight: DEFAULT_WEIGHTS[el] };
    }
    return init;
  });

  const [previewTexts, setPreviewTexts] = useState({ ...DEFAULT_PREVIEWS });
  const [allFamilies, setAllFamilies] = useState([]);
  const [loadingFonts, setLoadingFonts] = useState(true);
  const [copied, setCopied] = useState(false);

  // Fetch ALL font families by paginating (server caps each page at 100)
  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      const families = new Set();
      let page = 1;
      while (true) {
        const data = await fontsApi.list({ sort: 'name', order: 'ASC', limit: 100, page });
        if (cancelled) return;
        const batch = data.fonts || [];
        batch.forEach(f => { const n = f.family || f.name; if (n) families.add(n); });
        if (batch.length < 100 || page >= (data.pagination?.pages || 1)) break;
        page++;
      }
      setAllFamilies([...families].sort((a, b) => a.localeCompare(b)));
      setLoadingFonts(false);
    }
    fetchAll().catch(() => setLoadingFonts(false));
    return () => { cancelled = true; };
  }, []);

  const setAssignment = (el, key, value) => {
    setAssignments(prev => ({ ...prev, [el]: { ...prev[el], [key]: value } }));
  };

  const setPreviewText = (el, text) => {
    setPreviewTexts(prev => ({ ...prev, [el]: text }));
  };

  const generateCSS = () => {
    let css = '/* FontVault CSS Export */\n\n';
    for (const el of ELEMENTS) {
      const a = assignments[el];
      if (!a.family) continue;
      const selector = el === 'Body' ? 'body' : el === 'Code' ? 'code, pre' : el.toLowerCase();
      css += `${selector} {\n`;
      css += `  font-family: '${a.family}', ${el === 'Code' ? 'monospace' : 'sans-serif'};\n`;
      css += `  font-size: ${a.size}px;\n`;
      css += `  font-weight: ${a.weight};\n`;
      css += `}\n\n`;
    }
    return css;
  };

  const handleExport = () => {
    navigator.clipboard.writeText(generateCSS());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const anyAssigned = ELEMENTS.some(el => assignments[el].family);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 1000, maxHeight: '88vh',
          background: theme.bgElevated, borderRadius: 16,
          border: `1px solid ${theme.borderMedium}`,
          boxShadow: theme.shadow, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.text }}>
            CSS Playground
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleExport}
              style={{
                padding: '7px 14px', fontSize: 11, fontWeight: 600,
                background: copied ? theme.greenBg : 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
                border: copied ? `1px solid ${theme.greenBorder}` : 'none',
                borderRadius: 7, color: copied ? theme.green : '#fff',
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
              }}
            >{copied ? '✓ Copied!' : 'Copy CSS'}</button>
            <button
              onClick={onClose}
              style={{
                background: theme.bgInput, border: 'none', borderRadius: 6,
                color: theme.textMuted, width: 28, height: 28, cursor: 'pointer', fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left: Controls */}
          <div style={{
            width: 300, borderRight: `1px solid ${theme.border}`,
            padding: 16, overflowY: 'auto', flexShrink: 0,
          }}>
            {loadingFonts && (
              <div style={{ fontSize: 11, color: theme.textFaint, marginBottom: 12 }}>
                Loading fonts…
              </div>
            )}
            {ELEMENTS.map(el => (
              <div key={el} style={{ marginBottom: 20 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 6,
                }}>{el}</div>

                {/* Font picker */}
                <FontPicker
                  value={assignments[el].family}
                  onChange={v => setAssignment(el, 'family', v)}
                  fontFamilies={allFamilies}
                  theme={theme}
                />

                {/* Size + Weight */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 9, color: theme.textGhost }}>Size</span>
                    <input
                      type="number" min={8} max={120} value={assignments[el].size}
                      onChange={e => setAssignment(el, 'size', parseInt(e.target.value) || 16)}
                      style={{
                        width: '100%', padding: '4px 6px',
                        background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                        borderRadius: 4, color: theme.text, fontSize: 11,
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 9, color: theme.textGhost }}>Weight</span>
                    <select
                      value={assignments[el].weight}
                      onChange={e => setAssignment(el, 'weight', parseInt(e.target.value))}
                      style={{
                        width: '100%', padding: '4px 6px',
                        background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                        borderRadius: 4, color: theme.text, fontSize: 11,
                        fontFamily: 'inherit', cursor: 'pointer',
                      }}
                    >
                      {[100,200,300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>

                {/* Preview text input */}
                <div style={{ position: 'relative' }}>
                  <span style={{ fontSize: 9, color: theme.textGhost }}>Preview text</span>
                  {el === 'Code' ? (
                    <textarea
                      value={previewTexts[el]}
                      onChange={e => setPreviewText(el, e.target.value)}
                      rows={2}
                      style={{
                        width: '100%', padding: '4px 6px',
                        background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                        borderRadius: 4, color: theme.text, fontSize: 11,
                        fontFamily: 'monospace', resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={previewTexts[el]}
                      onChange={e => setPreviewText(el, e.target.value)}
                      placeholder={DEFAULT_PREVIEWS[el]}
                      style={{
                        width: '100%', padding: '4px 6px',
                        background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                        borderRadius: 4, color: theme.text, fontSize: 11,
                        fontFamily: 'inherit', boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Right: Live Preview */}
          <div style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
            <div style={{
              background: '#fff',
              borderRadius: 12, padding: 32,
              color: '#1a1a1e', minHeight: 300,
              border: `1px solid ${theme.borderMedium}`,
            }}>
              {assignments.H1.family && (
                <h1 style={{
                  fontFamily: `'${assignments.H1.family}', sans-serif`,
                  fontSize: assignments.H1.size, fontWeight: assignments.H1.weight,
                  marginBottom: 12, lineHeight: 1.2,
                }}>{previewTexts.H1 || DEFAULT_PREVIEWS.H1}</h1>
              )}
              {assignments.H2.family && (
                <h2 style={{
                  fontFamily: `'${assignments.H2.family}', sans-serif`,
                  fontSize: assignments.H2.size, fontWeight: assignments.H2.weight,
                  marginBottom: 10, lineHeight: 1.3,
                }}>{previewTexts.H2 || DEFAULT_PREVIEWS.H2}</h2>
              )}
              {assignments.H3.family && (
                <h3 style={{
                  fontFamily: `'${assignments.H3.family}', sans-serif`,
                  fontSize: assignments.H3.size, fontWeight: assignments.H3.weight,
                  marginBottom: 8, lineHeight: 1.3,
                }}>{previewTexts.H3 || DEFAULT_PREVIEWS.H3}</h3>
              )}
              {assignments.Body.family && (
                <p style={{
                  fontFamily: `'${assignments.Body.family}', sans-serif`,
                  fontSize: assignments.Body.size, fontWeight: assignments.Body.weight,
                  lineHeight: 1.6, marginBottom: 16, color: '#333',
                }}>{previewTexts.Body || DEFAULT_PREVIEWS.Body}</p>
              )}
              {assignments.Code.family && (
                <pre style={{
                  fontFamily: `'${assignments.Code.family}', monospace`,
                  fontSize: assignments.Code.size, fontWeight: assignments.Code.weight,
                  background: '#f4f4f4', padding: 16, borderRadius: 8,
                  overflow: 'auto', color: '#333', whiteSpace: 'pre-wrap',
                }}>{previewTexts.Code || DEFAULT_PREVIEWS.Code}</pre>
              )}
              {!anyAssigned && (
                <div style={{ color: '#999', textAlign: 'center', padding: 40 }}>
                  Select fonts from the left panel to preview
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
